import React, { useMemo } from 'react';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import { Alert, Box, CircularProgress, IconButton, Tooltip } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import { DATA_GRID_DEFAULT_PAGE_SIZE } from './DataGridNumberedPagination';
import { DataGrid, type GridColDef } from '@mui/x-data-grid';
import {
  formatBakuMutuLine,
  formatJenisPengukuranLine,
  coercePositiveIntField,
  isLikelyStrapiDocumentId,
  parseStoredParameterUjiDocumentIds,
  type StrapiSampleInformationRow,
} from '../api/strapiCrm';
import { useAuth } from '../auth/AuthContext';
import { useParameterUjisByDocumentIdsQuery } from '../query/hooks';
import { formatSampleParameterUjiPlain } from '../utils/sampleParameterDisplay';
import { ChemParameterUjiCell } from './ChemParameterUjiCell';

function shortId(documentId: string): string {
  if (documentId.length <= 12) return documentId;
  return `${documentId.slice(0, 8)}…`;
}

function formatDate(s?: string | null): string {
  if (!s) return '';
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return s;
  return d.toLocaleDateString('id-ID');
}

type SampleInformationsDataGridProps = {
  rows: StrapiSampleInformationRow[];
  isLoading: boolean;
  isError: boolean;
  error?: Error | null;
};

export function SampleInformationsDataGrid({
  rows,
  isLoading,
  isError,
  error,
}: SampleInformationsDataGridProps) {
  const { jwt } = useAuth();

  const allParameterUjiDocIds = useMemo(() => {
    const set = new Set<string>();
    for (const r of rows) {
      for (const t of parseStoredParameterUjiDocumentIds(r.parameter_uji ?? '')) {
        if (isLikelyStrapiDocumentId(t)) set.add(t.trim());
      }
    }
    return [...set];
  }, [rows]);

  const parameterUjisLookupQ = useParameterUjisByDocumentIdsQuery(jwt, allParameterUjiDocIds);
  const parameterUjisMap = parameterUjisLookupQ.data ?? new Map();

  const columns: GridColDef<StrapiSampleInformationRow>[] = useMemo(
    () => [
      {
        field: 'documentId',
        headerName: 'ID sampel',
        minWidth: 116,
        flex: 0.4,
        valueFormatter: (v) => shortId(String(v)),
      },
      {
        field: 'jenis_pengukuran',
        headerName: 'Jenis pengukuran',
        minWidth: 110,
        flex: 0.45,
        valueGetter: (_, row) => formatJenisPengukuranLine(row.jenis_pengukuran),
      },
      {
        field: 'parameter_uji',
        headerName: 'Parameter uji',
        minWidth: 130,
        flex: 0.5,
        sortable: false,
        valueGetter: (_, row) => formatSampleParameterUjiPlain(row.parameter_uji),
        renderCell: (params) => (
          <ChemParameterUjiCell raw={params.row.parameter_uji} puLookup={parameterUjisMap} />
        ),
      },
      {
        field: 'baku_mutu',
        headerName: 'Baku mutu',
        minWidth: 110,
        flex: 0.45,
        valueGetter: (_, row) => formatBakuMutuLine(row.baku_mutu),
      },
      {
        field: 'sample_point_count',
        headerName: 'Jml. titik',
        minWidth: 90,
        flex: 0.3,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueGetter: (_, row) => coercePositiveIntField(row.sample_point_count as unknown) ?? null,
      },
      {
        field: 'harga',
        headerName: 'Harga',
        minWidth: 100,
        flex: 0.32,
        type: 'number',
        align: 'right',
        headerAlign: 'right',
        valueFormatter: (v) => (v != null && v !== '' ? String(v) : '—'),
      },
      {
        field: 'work_order_ref',
        headerName: 'Work order',
        minWidth: 110,
        flex: 0.4,
        sortable: false,
        valueGetter: (_, row) => row.work_order?.documentId ?? '',
        valueFormatter: (v) => (v ? shortId(String(v)) : '—'),
      },
      {
        field: 'quotation_ref',
        headerName: 'Quotation',
        minWidth: 160,
        flex: 0.65,
        sortable: false,
        valueGetter: (_, row) => {
          const wo = row.work_order;
          const qDirect = row.quotation ?? wo?.quotation;
          if (!wo?.documentId && !qDirect) return '';
          const job = wo?.job_number?.trim() ? wo.job_number : '—';
          const qn = qDirect?.quotation_number ?? '—';
          return `${job} · ${qn}`;
        },
      },
      {
        field: 'wo_date',
        headerName: 'Tgl. WO',
        minWidth: 110,
        flex: 0.38,
        sortable: false,
        valueGetter: (_, row) => row.work_order?.work_order_date ?? '',
        valueFormatter: (v) => (v ? formatDate(String(v)) : '—'),
      },
      {
        field: 'createdAt',
        headerName: 'Dibuat',
        minWidth: 110,
        flex: 0.38,
        valueFormatter: (v) => (v ? formatDate(String(v)) : '—'),
      },
      {
        field: '__wo_link',
        headerName: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        width: 52,
        align: 'center',
        headerAlign: 'center',
        renderCell: ({ row }) => {
          const qid = row.quotation?.documentId ?? row.work_order?.quotation?.documentId;
          if (!qid) return null;
          return (
            <Tooltip title="Buka quotation">
              <IconButton
                component={RouterLink}
                to={`/quotation/${encodeURIComponent(qid)}/view`}
                size="small"
                aria-label="Buka quotation"
                onClick={(e) => e.stopPropagation()}
              >
                <OpenInNewOutlinedIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          );
        },
      },
    ],
    [parameterUjisMap],
  );

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" py={4}>
        <CircularProgress size={32} />
      </Box>
    );
  }
  if (isError) {
    return (
      <Alert severity="error">
        {error instanceof Error ? error.message : 'Gagal memuat informasi sampel.'}
      </Alert>
    );
  }

  return (
    <Box sx={{ width: '100%' }} data-testid="sample-informations-data-grid">
      <DataGrid
        rows={rows}
        columns={columns}
        getRowId={(r) => r.documentId}
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25, 50, 100]}
        initialState={{ pagination: { paginationModel: { pageSize: DATA_GRID_DEFAULT_PAGE_SIZE, page: 0 } } }}
        autoHeight
        density="compact"
        sx={{ '& .MuiDataGrid-columnHeaders': { fontWeight: 600 } }}
      />
    </Box>
  );
}
